import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProjetoComentario {
  id: number;
  projeto_id: number;
  usuario_id?: string | null;
  conteudo: string;
  created_at: string;
  updated_at: string;
  usuario?: {
    id: string;
    nome?: string | null;
    email?: string | null;
  } | null;
}

export function useProjetoComentarios(projetoId: number) {
  return useQuery({
    queryKey: ["projeto-comentarios", projetoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projeto_comentarios")
        .select("*")
        .eq("projeto_id", projetoId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar comentários do projeto:", error);
        throw error;
      }

      const rows = (data || []) as ProjetoComentario[];
      const userIds = Array.from(
        new Set(rows.map((r) => r.usuario_id).filter(Boolean) as string[])
      );

      if (userIds.length === 0) return rows;

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, nome, email")
        .in("id", userIds);

      const byId = new Map((profiles || []).map((p) => [p.id, p]));
      return rows.map((r) => ({
        ...r,
        usuario: r.usuario_id ? byId.get(r.usuario_id) || null : null,
      }));
    },
    enabled: !!projetoId,
    staleTime: 30_000,
  });
}

export function useCriarProjetoComentario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projetoId,
      conteudo,
    }: {
      projetoId: number;
      conteudo: string;
    }) => {
      const texto = conteudo.trim();
      if (!texto) throw new Error("Informe o comentário.");

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("projeto_comentarios")
        .insert({
          projeto_id: projetoId,
          usuario_id: user?.id ?? null,
          conteudo: texto,
        })
        .select("*")
        .single();

      if (error) {
        console.error("Erro ao criar comentário:", error);
        throw new Error(error.message || "Erro ao criar comentário");
      }

      return data as ProjetoComentario;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["projeto-comentarios", data.projeto_id],
      });
    },
  });
}

export function useExcluirProjetoComentario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      projetoId,
    }: {
      id: number;
      projetoId: number;
    }) => {
      const { error } = await supabase
        .from("projeto_comentarios")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Erro ao excluir comentário:", error);
        throw new Error(error.message || "Erro ao excluir comentário");
      }

      return { id, projetoId };
    },
    onSuccess: ({ projetoId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projeto-comentarios", projetoId],
      });
    },
  });
}
